import React, { useCallback } from 'react';
import { formatTranslation } from 'intl';
import { InspectionTranslationKeys, CommonTranslationKeys } from 'intl/keys';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { NumericUnit, numericUnits } from 'shared/domain/checklistAnswer/types/numericUnit';
import { MemoAnswerCardHeader } from './AnswerCardHeader';
import { AnswerPanelProps, NumericAnswerConfig } from './types';
import { ANSWER_FIELD_ROW_SX } from './styles';
import { getAnswerCardSx } from './answerTypeTheme';
import { formatFieldError } from './formatFieldError';

export function NumericAnswerPanel({
  answerId,
  answer,
  fieldErrors,
  onUpdate,
  onDelete,
}: AnswerPanelProps<NumericAnswerConfig>): React.ReactElement {
  const intl = useIntl();
  const isSliderConfigRequired =
    answer.step != null ||
    answer.rangeMin != null ||
    answer.rangeMax != null;

  const handleUpdate = useCallback(
    (updates: Partial<NumericAnswerConfig>) => {
      onUpdate(answerId, updates);
    },
    [answerId, onUpdate]
  );

  const handleDelete = useCallback(() => {
    onDelete(answerId);
  }, [answerId, onDelete]);

  const handleLabelChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleUpdate({ label: event.target.value });
    },
    [handleUpdate]
  );

  const handleRequiredChange = useCallback(
    (isRequired: boolean) => {
      handleUpdate({ isRequired });
    },
    [handleUpdate]
  );

  const handleUnitChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value;
      handleUpdate({ unit: value === '' ? undefined : value });
    },
    [handleUpdate]
  );

  const handleNumberChange = useCallback(
    (field: 'normMin' | 'normMax' | 'rangeMin' | 'rangeMax' | 'step') =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        handleUpdate({
          [field]: value === '' ? undefined : parseFloat(value),
        } as Partial<NumericAnswerConfig>);
      },
    [handleUpdate]
  );

  return (
    <Box sx={getAnswerCardSx(answer.type)}>
      <MemoAnswerCardHeader
        answerType={answer.type}
        isRequired={answer.isRequired}
        onRequiredChange={handleRequiredChange}
        onDelete={handleDelete}
      />

      <TextField
        label={formatTranslation(intl, InspectionTranslationKeys.label)}
        value={answer.label}
        onChange={handleLabelChange}
        size="medium"
        fullWidth
        required
        error={Boolean(fieldErrors?.label)}
        helperText={formatFieldError(intl, fieldErrors?.label)}
        sx={{ mb: 2 }}
      />

      <FormControl size="medium" fullWidth sx={{ mb: 2 }}>
        <InputLabel>{formatTranslation(intl, InspectionTranslationKeys.unit)}</InputLabel>
        <Select
          value={answer.unit || ''}
          onChange={handleUnitChange}
          label={formatTranslation(intl, InspectionTranslationKeys.unit)}
        >
          <MenuItem value="">
            <em>{formatTranslation(intl, CommonTranslationKeys.none)}</em>
          </MenuItem>
          {numericUnits.map((unit) => (
            <MenuItem key={unit} value={unit}>
              {unit}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={ANSWER_FIELD_ROW_SX}>
        <TextField
          label={formatTranslation(intl, InspectionTranslationKeys.normMin)}
          type="number"
          value={answer.normMin ?? ''}
          onChange={handleNumberChange('normMin')}
          size="medium"
          error={Boolean(fieldErrors?.normMin)}
          helperText={formatFieldError(intl, fieldErrors?.normMin)}
        />
        <TextField
          label={formatTranslation(intl, InspectionTranslationKeys.normMax)}
          type="number"
          value={answer.normMax ?? ''}
          onChange={handleNumberChange('normMax')}
          size="medium"
          error={Boolean(fieldErrors?.normMax)}
          helperText={formatFieldError(intl, fieldErrors?.normMax)}
        />
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 1 }}>
        {formatTranslation(intl, InspectionTranslationKeys.sliderConfigLabel)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {formatTranslation(intl, InspectionTranslationKeys.sliderConfigHelper)}
      </Typography>

      <TextField
        label={formatTranslation(intl, InspectionTranslationKeys.step)}
        type="number"
        value={answer.step ?? ''}
        onChange={handleNumberChange('step')}
        size="medium"
        fullWidth
        required={isSliderConfigRequired}
        error={Boolean(fieldErrors?.step)}
        helperText={formatFieldError(intl, fieldErrors?.step)}
        sx={{ mb: 2 }}
        inputProps={{ min: 0, step: 'any' }}
      />

      <Box sx={ANSWER_FIELD_ROW_SX}>
        <TextField
          label={formatTranslation(intl, InspectionTranslationKeys.rangeMin)}
          type="number"
          value={answer.rangeMin ?? ''}
          onChange={handleNumberChange('rangeMin')}
          size="medium"
          required={isSliderConfigRequired}
          error={Boolean(fieldErrors?.rangeMin)}
          helperText={formatFieldError(intl, fieldErrors?.rangeMin)}
        />
        <TextField
          label={formatTranslation(intl, InspectionTranslationKeys.rangeMax)}
          type="number"
          value={answer.rangeMax ?? ''}
          onChange={handleNumberChange('rangeMax')}
          size="medium"
          required={isSliderConfigRequired}
          error={Boolean(fieldErrors?.rangeMax)}
          helperText={formatFieldError(intl, fieldErrors?.rangeMax)}
        />
      </Box>
    </Box>
  );
}

export const MemoNumericAnswerPanel = React.memo(NumericAnswerPanel);
